import React, {useEffect, useState} from "react";

import {
	Bill,
	BillLineList,
	BillRestControllerApiFactory,
	BudgetList,
	BudgetRestControllerApiFactory,
	CustomerRestControllerApiFactory
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {types} from "../../../../components/form/types";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {fileLocalSave} from "../../../../components/form/form_inputs/form_file";
import {useApiManager} from "../../../../components/utils/api";
import {useNavigate} from "react-router-dom";
import {numberToString} from "../../../../components/utils/numbers";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import dayjs from "dayjs";
import {btnSx, EmptyState, euros, Page, PageHeader, Panel, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {RecordAttachments} from "../../../../components/record_attachments";
import {billStatus} from "./labels";


export const useCustomerNotes = (_customerId: number | undefined = undefined) => {
	const apiManager = useApiManager();
	const customerCtrl = apiManager.factory(CustomerRestControllerApiFactory);
	const [customerNotes, setCustomerNotes] = useState<string>("");
	const [customerId, setCustomerId] = useState<number | undefined>(_customerId);

	const loadCustomerNotes = () => {
		if (customerId) {
			apiManager.executeRaw(customerCtrl.getCustomer(customerId)).then((resp) => {
				setCustomerNotes(resp.data?.observation || "");
			});
		} else {
			setCustomerNotes("");
		}
	}

	useEffect(() => {
		loadCustomerNotes();
	}, [customerId]);

	useEffect(() => {
		loadCustomerNotes();
	}, []);

	return {customerNotes, setCustomerId};
}

export const BillEditPage = () => {
	const navigate = useNavigate();
	const apiManager = useApiManager();

	const ctrl = apiManager.factory(BillRestControllerApiFactory);
	const budgetCtrl = apiManager.factory(BudgetRestControllerApiFactory);
	const {customerNotes, setCustomerId} = useCustomerNotes();
	const [selectLine, setSelectLine] = useState<BillLineList>();
	const [budgets, setBudgets] = useState<BudgetList[]>([]);

	useEffect(() => {
		types.budgetsUncompleted.supplier(apiManager).then(resp => setBudgets(resp));
		// apiManager.execute(budgetCtrl.listBudget()).then(resp => setBudgets(resp))
	}, []);

	const form = useForm({
		fetcher: (id) => {
			const promise = ctrl.getBill(id);
			promise.then(resp => setCustomerId(resp.data.customer.id));
			return promise;
		},
		updater: (bean) => ctrl.saveBill(bean),
		remover: (id) => ctrl.deleteOrDismissBill(id),
		factory: () => {
			return {
				template: {id: 50, name: "Factura Estándar"}
			} as Bill;
		},
		extraActions: [
			{
				label: "Duplicar",
				action: () => {
					const id = form.getId();
					if (confirm("Guarde lo cambios antes de duplicar.\n" +
						"La pantalla se posicionará sobre el registro nuevo.\n\n" +
						"¿Seguro que desea duplicar la factura?") && id) {
						apiManager.executeRaw(ctrl.duplicateBill(id)).then((resp) => {
							navigate("/app/admin/billing/bill/" + resp.data.id);
							window.location.reload();
						});
					}
				},
			},
			{
				label: "Expedir",
				action: () => {
					const id = form.getId();
					if (id && confirm("Expedir genera el registro VERI*FACTU de la factura.\n\n" +
						"Después su número, fecha, cliente, tipo, descripción y líneas ya no se pueden cambiar:" +
						" se corrige con una rectificativa.\n\nGuarde antes los cambios. ¿Expedir?")) {
						apiManager.execute(ctrl.issueBill(id)).then(() => form.load());
					}
				},
			},
			{
				label: "Anular",
				action: () => {
					const id = form.getId();
					if (id && confirm("Anular genera un registro VERI*FACTU de anulación: es para una factura" +
						" que no debió expedirse. Si lo que falla es el importe o un dato, se hace una rectificativa." +
						"\n\n¿Anular la factura?")) {
						apiManager.execute(ctrl.cancelBill(id)).then(() => form.load());
					}
				},
			},
			{
				label: "Pdf",
				action: () => {
					const id = form.getId();
					if (id) {
						apiManager.executeRaw(ctrl.viewPdfBill(id)).then((resp) => {
							fileLocalSave(resp);
						});
					}
				},
			},
			{
				label: "e-Factura",
				action: () => {
					const id = form.getId();
					if (id) {
						if (confirm("Esta opción descarga un XML que debe firmarse con AutoFirma, obteniendo un XSIG que debe subirse a Face. Deseas continuar?"))
							apiManager.executeRaw(ctrl.viewFacturaEBill(id)).then((resp) => {
								fileLocalSave(resp);
							});
					}
				},
			},
		],
	} as UseFormProps<Bill>);


	const id = form.getId();
	const totals = form.propModel.value?.totals;
	const lines: BillLineList[] = form.value("lines") || [];
	const issued = !!form.value("issuedAt");
	const customerName = form.value("customer")?.shortName || form.value("customer")?.name;
	// Solo los presupuestos del cliente de la factura (#14463); antes salían los de todos.
	const customerBudgets = budgets.filter(b => !form.value("customer")?.id || b.customer?.id === form.value("customer")?.id);
	const status = id ? billStatus(form.propModel.value || {} as Bill) : undefined;
	const nf = (n?: number | null) => n === undefined || n === null ? "" : n.toLocaleString("es-ES", {maximumFractionDigits: 2});

	// Una fila de importe del resumen: etiqueta a la izquierda y cifra alineada a la derecha.
	const TotalRow = ({label, value, strong}: { label: string, value: string, strong?: boolean }) => (
		<Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{py: 0.75}}>
			<Typography variant="body2" color={strong ? "text.primary" : "text.secondary"} fontWeight={strong ? 700 : 400}>{label}</Typography>
			<Typography variant={strong ? "h6" : "body2"} fontWeight={strong ? 700 : 600} sx={{fontVariantNumeric: "tabular-nums"}}>{value}</Typography>
		</Stack>
	);

	return (
		<Page>
			<PageHeader back="/app/admin/billing/bill"
						title={id ? `Factura ${form.value("number") ?? ""}`.trim() : "Nueva factura"}
						subtitle={id ? (
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
								{customerName && <span>{customerName}</span>}
								{status && <SoftChip label={status.label} tone={status.tone}/>}
								<SoftChip tone={issued ? "info" : "neutral"}
										  icon={issued ? <LockOutlinedIcon/> : undefined}
										  label={issued
											  ? "Expedida el " + dayjs(form.value("issuedAt")).format("DD/MM/YYYY HH:mm")
											  : "Borrador: sin registro VERI*FACTU"}/>
							</Stack>
						) : undefined}/>

			<FormContent form={form} withoutPaper>

				<Grid container spacing={3}>
					<Grid item xs={12} md={9}>
						<Stack spacing={3}>
							{/*
							  Expedida = tiene registro VERI*FACTU y sus datos fiscales están bloqueados
							  (#13059). El backend lo protege aunque se edite aquí: esto solo lo avisa.
							*/}
							{issued && (
								<Box sx={{px: 2, py: 1.25, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider"}}>
									<Typography variant="body2" color="text.secondary">
										Expedida: número, fecha, cliente, tipo, descripción y líneas ya no se pueden cambiar; se corrige con una rectificativa.
									</Typography>
								</Box>
							)}

							<Panel title="Factura">
								<Grid container spacing={2}>
									<Grid item xs={6} sm={3}>
										<FormInput type={"number"} field={"number"} label={"Número"}/>
									</Grid>
									<Grid item xs={6} sm={3}>
										<FormInput field={"code"} label={"Código"}/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<FormSelect field={"template"} label={"Plantilla"} type={types.templates}/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<FormToggle field={"doubtful"} label={"Incobrable"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormSelect field={"invoiceType"} label={"Tipo"} type={types.invoiceTypes}
													helperText={"F1 normal; R1 rápeles y descuentos, R4 resto de rectificativas"}/>
									</Grid>
									<Grid item xs={12} sm={8}>
										{/* Solo si es rectificativa: a quién rectifica (#13098). */}
										{String(form.value("invoiceType") ?? "F1").startsWith("R") && (
											<FormAutocomplete field={"rectifiedBill"} label={"Rectifica a"} type={types.bills}/>
										)}
									</Grid>
									<Grid item xs={12} sm={5}>
										<FormAutocomplete
											field={"customer"}
											label={"Cliente"}
											type={types.customers}
											onChange={(type, value) => setCustomerId(value?.id)}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormAutocomplete
											field={"organizationUnit"}
											label={"Unidad"}
											type={types.organizationUnits}
										/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<FormToggle field={"avoidDiscounts"} label={"No descontable"}/>
									</Grid>
								</Grid>
							</Panel>

							<Panel title="Fechas y cobro">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={4}>
										<FormInput field={"issueDate"} type={"date"} label={"Emisión"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"dueDate"} type={"date"} label={"Vencimiento"}
												   helperText={"Se calcula con el plazo del cliente; se puede pactar otro"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormSelect field={"paymentMethod"} label={"Forma de cobro"}
													type={types.paymentMethods}/>
									</Grid>
									{form.value("paymentMethod") === "ADEUDO_SEPA" && (
										<Grid item xs={12} sm={8}>
											{/*
											  El item serializado del mandato lleva {id, reference}: el campo
											  marcado con @JsonItemize es `reference`, no `name`. Leer `.name`
											  aquí compilaba igual y habría mostrado siempre «sin mandato».
											*/}
											<TextField
												fullWidth
												size={"small"}
												label={"Mandato SEPA que ampara el cobro"}
												value={form.value("sepaMandate")?.reference || "— sin mandato —"}
												disabled
												helperText={"Se asigna solo al guardar, con el mandato activo del cliente"}/>
										</Grid>
									)}
									<Grid item xs={12} sm={3}>
										<FormInput field={"sentDate"} type={"date"} label={"Enviada"}/>
									</Grid>
									<Grid item xs={12} sm={3}>
										{/* Cierre a mes vencido: la factura es de julio y el servicio de junio (#13101). */}
										<FormInput field={"operationDate"} type={"date"} label={"Fecha de operación"}
												   helperText={"Solo si el servicio es de otro mes que la factura"}/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<FormInput field={"advanceDate"} type={"date"} label={"Anticipada"}/>
									</Grid>
									<Grid item xs={12} sm={3}>
										<FormInput field={"paymentDate"} type={"date"} label={"Pago"}/>
									</Grid>
								</Grid>
							</Panel>

							<Panel title="FACe · Administración pública"
								   subtitle="Si solo se indica el primer código, se usa el mismo en los tres.">
								<Grid container spacing={2}>
									<Grid item xs={12} sm={4}>
										<FormInput field={"faceCodContable"} type={"text"} label={"Código contable"}
												   helperText={"Formato: A04035971"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"faceCodGestor"} type={"text"} label={"Código gestor"}/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<FormInput field={"faceCodTramitadora"} type={"text"} label={"Código tramitadora"}/>
									</Grid>
								</Grid>
							</Panel>

							<Panel title="Textos">
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<FormInput field={"description"} type={"text"} label={"Descripción"} rows={3}/>
									</Grid>
									<Grid item xs={12} md={6}>
										<FormInput field={"comments"} type={"text"} label={"Comentarios"} rows={3}/>
									</Grid>
								</Grid>
							</Panel>

							{id && (
								<Panel title="Líneas" subtitle="Pulsa una línea para cambiarla o borrarla."
									   action={<Button size="small" variant="outlined" startIcon={<AddRoundedIcon/>} sx={btnSx}
													   onClick={() => setSelectLine({bill: {id: id}} as BillLineList)}>
										   Nueva línea
									   </Button>}>
									{lines.length === 0 ? (
										<EmptyState title="Sin líneas" description="Añade la primera con «Nueva línea»."/>
									) : (
										<TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
											<Table size="small" sx={{...tableSx, minWidth: 640}}>
												<TableHead>
													<TableRow>
														<TableCell width={"10%"}>Código</TableCell>
														<TableCell width={"22%"}>Presupuesto</TableCell>
														<TableCell>Concepto</TableCell>
														<TableCell width={"10%"} align="right">Cantidad</TableCell>
														<TableCell width={"13%"} align="right">Precio neto</TableCell>
														<TableCell width={"8%"} align="right">IVA</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{lines.map((line: BillLineList) => (
														<TableRow key={"line-" + line.id} hover sx={{cursor: "pointer"}}
																  onClick={() => setSelectLine(line)}>
															<TableCell sx={{fontWeight: 600}}>{line.code}</TableCell>
															<TableCell sx={{color: "text.secondary"}}>{line.budget ? types.budgets.labeler(line.budget) : ""}</TableCell>
															<TableCell>{line.concept}</TableCell>
															<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{nf(line.quantity)}</TableCell>
															<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{euros(line.unitNetPrice)}</TableCell>
															<TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{line.taxType != null ? nf(line.taxType) + " %" : ""}</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									)}
								</Panel>
							)}
						</Stack>
					</Grid>

					<Grid item xs={12} md={3}>
						<Stack spacing={3} sx={{position: {md: "sticky"}, top: {md: 16}}}>
							{totals && (
								<Panel title="Importes">
									<TotalRow label="Cantidad" value={numberToString(totals.quantity)}/>
									<TotalRow label="Base imponible" value={euros(totals.subTotal)}/>
									<TotalRow label="IVA" value={euros(totals.taxes)}/>
									<Box sx={{borderTop: "1px solid", borderColor: "divider", mt: 1, pt: 1}}>
										<TotalRow label="Total" value={euros(totals.total)} strong/>
									</Box>
								</Panel>
							)}
							<Panel title="Notas del cliente">
								<Typography variant="body2" color={customerNotes ? "text.primary" : "text.disabled"}
											sx={{whiteSpace: "pre-wrap", fontSize: 13}}>
									{customerNotes || "Sin notas."}
								</Typography>
							</Panel>
						</Stack>
					</Grid>
				</Grid>

			</FormContent>

			<Box sx={{mt: 3}}>
				<RecordAttachments recordType="BILL" recordId={id}/>
			</Box>

			<Dialog open={selectLine != undefined} onClose={() => setSelectLine(undefined)} fullWidth maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}>
				<DialogTitle sx={{fontWeight: 700}}>{selectLine?.id ? "Línea de factura" : "Nueva línea"}</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{pt: 1}}>
						<Grid item xs={6}>
							<TextField type={"number"} fullWidth label={"Posición"}
									   value={selectLine?.pos ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   pos: parseInt(e.target.value)
									   })}/>
						</Grid>
						<Grid item xs={6}>
							<TextField type={"number"} fullWidth label={"Código"}
									   value={selectLine?.code ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   code: e.target.value
									   })}/>
						</Grid>
						<Grid item xs={12}>
							<FormControl fullWidth>
								<InputLabel id="bill-line-budget">Presupuesto</InputLabel>
								<Select labelId="bill-line-budget" label="Presupuesto"
										value={selectLine?.budget?.id ?? ""}
										onChange={e => setSelectLine({
											...selectLine as BillLineList,
											budget: budgets.find(s => s.id === e.target.value)
										})}>
									<MenuItem value={''}><em>Sin presupuesto</em></MenuItem>
									{customerBudgets.map(b => (
										<MenuItem key={"budget-" + b.id} value={b.id}>
											{types.budgets.labeler(b)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{/* Sin presupuesto, la línea no cuenta en lo facturado del presupuesto (#14463). */}
							{!selectLine?.budget && customerBudgets.length > 0 && (
								<Alert severity="warning" sx={{mt: 1, borderRadius: 2}}>
									Este cliente tiene presupuestos abiertos. Sin presupuesto, esta línea no cuenta en lo facturado de ninguno.
								</Alert>
							)}
						</Grid>
						<Grid item xs={12}>
							<TextField type={"text"} fullWidth label={"Concepto"}
									   value={selectLine?.concept ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   concept: e.target.value
									   })}/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField type={"number"} fullWidth label={"Cantidad"}
									   value={selectLine?.quantity ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   quantity: parseFloat(e.target.value)
									   })}/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField type={"number"} fullWidth label={"Precio neto"}
									   value={selectLine?.unitNetPrice ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   unitNetPrice: parseFloat(e.target.value)
									   })}/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField type={"number"} fullWidth label={"IVA (%)"}
									   value={selectLine?.taxType ?? ""}
									   onChange={e => setSelectLine({
										   ...selectLine as BillLineList,
										   taxType: parseFloat(e.target.value)
									   })}/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{px: 3, pb: 2.5}}>
					{selectLine?.id && (
						<Button color="error" sx={{...btnSx, mr: "auto"}}
								onClick={() => {
									if (confirm("Seguro que deseas borrar la línea?")) {
										setSelectLine(undefined);
										apiManager
										.execute(ctrl.removeLinesBill(form.getId() as number, [(selectLine as BillLineList).id as number]))
										.then(resp => form.load());
									}
								}}>
							Borrar
						</Button>
					)}
					<Button onClick={() => setSelectLine(undefined)} sx={btnSx}>Cancelar</Button>
					<Button variant={"contained"} sx={btnSx}
							onClick={() => {
								const line = selectLine as BillLineList;
								// line.pos = ((form.value("lines")||[]).length || 0) + 1;
								// line.code = line.pos.toString();
								line.bill = {id: form.getId()} as Bill;
								apiManager
								.execute(ctrl.saveLineBill(form.getId() as number, line))
								.then(() => {
									setSelectLine(undefined);
									return form.load()
								});
							}}>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>
		</Page>
	);
};
